import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { makeTestId } from '../../utils/Declarative';
import { PropsAndElementAttrs, useResizeObserver } from '../../utils/ReactHelpers';
import { EmptyRect, SkeletonLoaderInfoContext } from './SkeletonLoaderInfoContext';
export { LoadingBone } from './LoadingBone';

export interface SkeletonLoaderProps {
  /**
   * A descriptive string to explain to assistive technology what the SkeletonLoaderWidget is waiting for.
   * Will only be shown when the SkeletonLaderWidget is in a pending state.
   * @default 'Waiting for content'
   *
   * Depending on what content is being loaded, consider using `SharedWidgets.LiveAnnouncer.announce()` to announce when/what content has loaded.
   */
  'aria-label'?: string;
  /**
   * Identifies the element (or elements) that labels the waiting indicator.
   * Will only be shown when the SkeletonLaderWidget is in a pending state.
   */
  'aria-labelledby'?: string;
  /**
   * The real content to display, once the SkeletonLoaderWidget is no longer in a `pending` state.
   * In rare cases, it may be useful to pass in `null`, if you'd like your conditional rendering logic to live
   * outside the SkeletonLoaderWidget.
   * Please ask in #tab-dev-shared-widgets if you think this applies to you.
   */
  children: Exclude<React.ReactNode, undefined>;
  /** Applies appropriate styles for displaying the `<LoadingBone/>`s over a dark background */
  darkMode?: boolean;
  /** One or more <LoadingBone/> components that should be displayed while in a `pending` state */
  fallback: React.ReactNode;
  /** When `true`, will show the `fallback` content, rather than the `children`. */
  isPending?: boolean;
  /** Added to the root element with the suffix `-SkeletonLoaderWidget` */
  testId?: string;
}

/**
 * A container for `<LoadingBone/>` components.
 * Used to orchestrate the shine animation across multiple `<LoadingBone/>`s.
 * By default, a SkeletonLoaderWidget will expand to 100% width. If you don't want this behavior, set `display: 'inline-block'` in your `style` prop.
 */
export const SkeletonLoaderWidget: React.FC<PropsAndElementAttrs<SkeletonLoaderProps>> = props => {
  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledby, children, darkMode, fallback, isPending, testId, ...restProps } = props;

  const [clientRect, setClientRect] = React.useState<DOMRect>(EmptyRect);
  const [containerEl, setContainerEl] = React.useState<HTMLDivElement | null>(null);

  const onResize = () => {
    setClientRect(containerEl?.getBoundingClientRect() || EmptyRect);
  };

  useResizeObserver(containerEl, onResize, isPending);

  const skeletonLoaderInfoContextData: SkeletonLoaderInfoContext = React.useMemo(() => ({
    clientRect,
    darkMode: !!darkMode,
    isInitialized: true
  }), [clientRect, darkMode]);

  return (
    <SkeletonLoaderInfoContext.Provider value={skeletonLoaderInfoContextData}>
      <div
        ref={setContainerEl}
        role={!isPending ? undefined : 'img'}
        aria-label={!isPending ? undefined : (ariaLabel || (ariaLabelledby ? undefined : Messages.skeletonLoaderLabel()))}
        aria-labelledby={!isPending ? undefined : ariaLabelledby}
        data-tb-test-id={makeTestId(testId, 'SkeletonLoaderWidget')}
        {...restProps}
      >
        {isPending ? fallback : children}
      </div>
    </SkeletonLoaderInfoContext.Provider>
  );
};
SkeletonLoaderWidget.displayName = 'SkeletonLoaderWidget';
